import React, { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import useVideos, { Query } from "../hooks/useVideos";
import useSnackbar from "../hooks/useSnackbar";
import {
  MAX_ALLOWED_VIDEO_UPLOADS,
  UPDATE_PROGRESS_ON_PERCENTAGE,
} from "../constants/constants";
import {
  useUploadDispatchContext,
  useUploadStateContext,
} from "../context/upload/upload.provider";
import { UploadActionEnums } from "../@types/upload.types";
import { Domain } from "../api/types";

const UploadVideoFilesComponent = ({
  showUploadPopup,
  publisherGroup,
  query,
  selectedDomain,
}: {
  showUploadPopup: boolean;
  publisherGroup?: string;
  query?: Query;
  selectedDomain: Domain | null;
}) => {
  const inputFileRef = useRef(null);
  const queryClient = useQueryClient();
  const { api } = useVideos({});
  const { snackbarSuccess, snackbarWarning } = useSnackbar();
  const uploadState = useUploadStateContext();
  const dispatch = useUploadDispatchContext();
  const { uploadingVideos, abortController } = uploadState;

  useEffect(() => {
    if (showUploadPopup === true) inputFileRef.current.click();
  }, [showUploadPopup]);

  const uploadDispatcher = (type: UploadActionEnums, payload?: any) => {
    if (type === UploadActionEnums.VIDEO_UPLOAD_PROGRESS && payload.progress) {
      if (!uploadingVideos[payload?.index]) return;
      const prevProgress = uploadingVideos[payload.index].progress;
      if (payload.progress - prevProgress >= UPDATE_PROGRESS_ON_PERCENTAGE) {
        dispatch({ type, payload });
      }
      return;
    }
    dispatch({ type, payload: payload || undefined });
  };

  const onFileSelected = React.useCallback(
    (acceptedFiles: any) => {
      const files = Array.from(acceptedFiles.target.files);
      if (files && files?.length > MAX_ALLOWED_VIDEO_UPLOADS) {
        snackbarWarning(
          `You can only upload ${MAX_ALLOWED_VIDEO_UPLOADS} video(s) at a time`,
        );
        return;
      }
      uploadDispatcher(UploadActionEnums.UPLOAD_STARTED, {
        uploadingFilesCount: files.length,
      });
      files.forEach((file: any, index) => {
        api.create(
          {
            file,
            index,
            abortController,
            uploadDispatcher,
            ...(publisherGroup && { publisherUsername: publisherGroup }),
            data: selectedDomain
              ? { domain: selectedDomain.domain }
              : undefined,
            onCreate: ({ token }, signedURLs) =>
              uploadDispatcher(UploadActionEnums.VIDEO_SIGNED_URL_CREATED, {
                token,
                index,
                videoURL: signedURLs.url,
                imageURL: signedURLs.imageURL,
              }),
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries(["videos", query]);
              snackbarSuccess(`Video Added to Library`);
            },
          },
        );
      });
    },
    [publisherGroup, selectedDomain],
  );

  return (
    <>
      <input
        type="file"
        multiple
        accept="video/*"
        ref={inputFileRef}
        style={{ display: "none" }}
        onChange={onFileSelected}
      />
    </>
  );
};

export default UploadVideoFilesComponent;
